import type { BrandName, FeedEvent } from './types';

/**
 * Brand display metadata.
 *
 * Hue per brand, confirmed 21 August 2026: Cashblack green, Afrofiliate
 * orange, EqualiShop violet. One brand, one hue, everywhere in the Shell,
 * so the map, the feed, the tiles and the charts agree. Assignment is
 * fixed: a filter that changes which brands are on screen never repaints
 * the survivors.
 *
 * The steps are the dataviz reference palette's dark-mode categorical
 * slots (green = slot 3, orange = slot 2, violet = slot 7), not chosen by
 * eye. The trio was validated all-pairs on the dark surface, which is the
 * gate a map has to clear: worst CVD dE 9.4 (orange vs green, deutan and
 * tritan, >= 8 target), worst normal-vision dE 24.6 (>= 15 floor),
 * contrast >= 3:1 on both #1a1a19 and the map's #101014.
 *
 * Afrofiliate's true brand orange #E97A0E is a brand MARK color, not a
 * data series, and stays in the wordmark accent only (--mark in
 * theme.css). Cashblack and EqualiShop have no documented brand palette;
 * these are Shell data-series steps chosen to satisfy the requested hues,
 * and they are not brand marks. If either brand later publishes a
 * palette, the mark color belongs in --mark, not here, and this trio has
 * to be re-validated as a set before any step moves.
 */
export const BRAND_META: Record<
  BrandName,
  { label: string; series: string; usersLabel: string; partnersLabel: string; txLabel: string; emptyReason: string }
> = {
  // emptyReason is what a zero tile SAYS (decision of 30 August 2026): an
  // accurate zero with no explanation reads as broken rather than early,
  // and the difference matters on a monitoring surface. The copy is
  // operator-owned and per brand, because the reason a tile is empty is a
  // business fact, not a UI guess: edit it here as each brand's reality
  // changes, and delete it for a brand once volume makes it moot.
  cashblack: {
    label: 'CashBlack',
    series: '#199e70',
    usersLabel: 'New members',
    partnersLabel: 'New merchants',
    txLabel: 'Transactions',
    emptyReason: 'waiting on the site rebuild',
  },
  afrofiliate: {
    label: 'Afrofiliate',
    series: '#d95926',
    usersLabel: 'New creators',
    partnersLabel: 'New brands',
    txLabel: 'Conversions',
    emptyReason: 'pipeline live, no traffic in this window yet',
  },
  equalishop: {
    label: 'EqualiShop',
    series: '#9085e9',
    usersLabel: 'New members',
    partnersLabel: 'New brands',
    // Counts transaction.recorded, same as the other two brands, so the
    // label says transactions. EqualiShop also emits redemption.recorded,
    // which is intent rather than money moving and deliberately does not
    // add to this tile; a redemption count wants its own tile, which is
    // an open item rather than a label that overstates this one.
    txLabel: 'Transactions',
    emptyReason: 'instrumentation still rolling out',
  },
};

export const BRAND_ORDER: BrandName[] = ['cashblack', 'afrofiliate', 'equalishop'];

/**
 * A geo cell holding more than one brand is not one brand's territory, so
 * it does not get a brand hue (§2.1.5 map, see GeoMap). A neutral is the
 * honest encoding: it says "no single brand owns this" rather than
 * implying an identity the data does not support.
 *
 * Deliberately outside the categorical hue space, and separated from the
 * three brand steps by lightness, which is the one channel every kind of
 * colour blindness keeps. It misses the validator's chroma floor on
 * purpose: that floor exists to stop a washed-out hue being used as an
 * identity, and this is the opposite, an explicit absence of identity.
 * Every separation check passes (worst normal-vision dE 19.4 against the
 * violet, contrast >= 3:1 on the map surface).
 *
 * Two neighbours were tried and rejected: a mid grey #898781 collides
 * with the green (normal-vision dE 12.6, under the 15 floor that
 * secondary encoding cannot excuse), and #a8a69b lands in the CVD warn
 * band against it (protan dE 7.4). A near-white #d8d6cc separates
 * cleanly but out-shouts the brand hues, which matters here because
 * alpha carries magnitude: a small mixed cell would read as loudly as a
 * large single-brand one.
 */
export const MIXED_SERIES = '#b9b7ac';

/** Cell hue lookup for the map: a brand step, or the neutral for mixed. */
export function seriesColor(key: BrandName | 'mixed'): string {
  return key === 'mixed' ? MIXED_SERIES : BRAND_META[key].series;
}

/** #rrggbb -> [r, g, b], for deck.gl layers which take channel arrays. */
export function toRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

export const EVENT_LABELS: Record<string, string> = {
  'click.recorded': 'Click',
  'conversion.recorded': 'Conversion',
  'conversion.approved': 'Conversion approved',
  'conversion.rejected': 'Conversion rejected',
  'conversion.reversed': 'Conversion reversed',
  'creator.joined': 'Creator joined',
  'advertiser.joined': 'Brand joined',
  'payout.completed': 'Payout sent',
  'member.joined': 'Member joined',
  'app.first_opened': 'App installed',
  'support.ticket_opened': 'Support ticket opened',
  'support.ticket_resolved': 'Support ticket resolved',
  'transaction.recorded': 'Transaction',
  'transaction.confirmed': 'Transaction confirmed',
  'transaction.rejected': 'Transaction rejected',
  'transaction.reversed': 'Transaction reversed',
  'cashback.paid': 'Cashback paid',
  'cashback.donated': 'Donation',
  'merchant.joined': 'Merchant joined',
  'brand.joined': 'Brand joined',
  'partner.activated': 'Partner activated',
  'redemption.recorded': 'Redemption',
  'subscription.started': 'Subscription started',
  'subscription.cancelled': 'Subscription cancelled',
};

export function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type;
}

/** "UK site" for a sign-up or purchase; "UK iPhone app" for an app install. */
export function marketWords(e: Pick<FeedEvent, 'event_type' | 'market' | 'os'>): string {
  if (e.event_type !== 'app.first_opened') return `${e.market} site`;
  const os = e.os === 'ios' ? 'iPhone' : e.os === 'android' ? 'Android' : null;
  return `${e.market} ${os ? `${os} app` : 'app'}`;
}
